/**
 * Sales by BU — gauge dashboard.
 *
 * For a selected month, each BU shows an actual-vs-target gauge with
 * GM% underneath. Target = Budget monthly income × (1 + stretch%).
 *
 * Reads GET /myob/budgets/sales-by-bu?month=YYYY-MM&stretch_pct=5
 */
import { useEffect, useMemo, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RefreshCw, TrendingUp } from "lucide-react";

/** Build a list of the last 24 (YYYY-MM) month keys, newest first. */
function useMonthOptions() {
  return useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      out.push({ key: `${y}-${m}`,
                 label: d.toLocaleString("en-AU", { month: "short", year: "numeric" }) });
    }
    return out;
  }, []);
}

/** Half-arc gauge — thin, red-vs-grey to match Power BI dashboard style. */
function Gauge({ value, target, size = 180 }) {
  const cx = size / 2, cy = size * 0.72;
  const r = size * 0.42;
  const strokeW = size * 0.10;
  const startAngle = 180, endAngle = 0;   // half-circle: 180° → 0°
  const pct = target > 0 ? Math.max(0, Math.min(1, value / target)) : 0;
  const currentAngle = startAngle - (startAngle - endAngle) * pct;

  const polar = (a, radius = r) => {
    const rad = (a * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy - radius * Math.sin(rad) };
  };
  const arcPath = (a1, a2) => {
    const p1 = polar(a1), p2 = polar(a2);
    const large = Math.abs(a1 - a2) > 180 ? 1 : 0;
    const sweep = a1 > a2 ? 1 : 0;
    return `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${large} ${sweep} ${p2.x} ${p2.y}`;
  };

  // Colour: green when at/over target, else red (Power BI convention)
  const fillColour = value >= target && target > 0 ? "#10b981" : "#ef4444";

  return (
    <svg width={size} height={size * 0.85} viewBox={`0 0 ${size} ${size * 0.85}`}>
      {/* Background arc (grey full range) */}
      <path d={arcPath(startAngle, endAngle)}
            stroke="#e5e7eb" strokeWidth={strokeW} fill="none" strokeLinecap="butt" />
      {/* Actual arc (green/red proportional) */}
      {pct > 0 && (
        <path d={arcPath(startAngle, currentAngle)}
              stroke={fillColour} strokeWidth={strokeW} fill="none" strokeLinecap="butt" />
      )}
      {/* Target end-marker */}
      {(() => {
        const p = polar(endAngle, r);
        return <circle cx={p.x} cy={p.y} r={2} fill="#111827" />;
      })()}
    </svg>
  );
}

export default function SalesByBUPanel() {
  const options = useMonthOptions();
  const [month, setMonth] = useState(options[1]?.key || options[0]?.key);   // default = previous month
  const [stretch, setStretch] = useState(5);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    if (!month) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get("/myob/budgets/sales-by-bu", {
        params: { month, stretch_pct: stretch },
      });
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load sales-by-BU");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, stretch]);

  return (
    <div className="space-y-4" data-testid="sales-by-bu-panel">
      {/* Header controls */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-lg p-3">
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-slate-800 text-sm flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            Sales vs Target — By Business Unit
          </div>
          <div className="text-[11px] text-slate-500">
            Target = Budget monthly revenue × (1 + stretch%). Default stretch is +5%.
          </div>
        </div>
        <label className="text-xs text-slate-600 flex items-center gap-1">
          Month
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="sbb-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="text-xs text-slate-600 flex items-center gap-1">
          Stretch
          <input
            type="number" min="0" max="50" step="1"
            value={stretch}
            onChange={(e) => setStretch(Math.max(0, Math.min(50, Number(e.target.value) || 0)))}
            className="w-16 h-8 px-2 text-xs border border-slate-200 rounded"
            data-testid="sbb-stretch-input"
          />
          <span>%</span>
        </label>
        <Button size="sm" variant="outline" onClick={load} disabled={busy}
                data-testid="sbb-refresh-btn" className="text-xs">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
      </div>

      {err && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          {err}
        </div>
      )}

      {data && (
        <>
          {/* Group total gauge */}
          <div className="bg-slate-800 rounded-lg text-white p-4"
               data-testid="sbb-group-header">
            <div className="text-center text-sm uppercase tracking-wider opacity-70">
              {formatMonthDisplay(data.month)} — Sales vs Target By Business Unit
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mt-4">
              <GaugeCard title="Total Sales vs Target"
                         value={data.group.sales}
                         target={data.group.target}
                         gmPct={data.group.gm_pct}
                         onDark
                         testid="sbb-group-gauge" />
            </div>
          </div>

          {/* Per-BU grid */}
          {data.rows.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500 italic"
                 data-testid="sbb-empty">
              No BUs to display — check that budgets have been imported for FY{data.fy_year} and MYOB invoices exist for {data.month}.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.rows.map((r) => (
                <GaugeCard
                  key={r.bu_id || r.bu_code}
                  title={`${r.bu_code} · ${r.bu_name}`}
                  value={r.sales}
                  target={r.target}
                  gmPct={r.gm_pct}
                  budget={r.budget}
                  attainment={r.attainment_pct}
                  testid={`sbb-gauge-${r.bu_code}`}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GaugeCard({ title, value, target, gmPct, budget, attainment, onDark = false, testid }) {
  const wrapCls = onDark
    ? "bg-transparent"
    : "bg-white border border-slate-200 rounded-lg p-3";
  const titleCls = onDark ? "text-white/90" : "text-slate-700";
  const metricCls = onDark ? "text-white" : "text-slate-900";
  return (
    <div className={wrapCls} data-testid={testid}>
      <div className={`text-xs font-medium ${titleCls}`}>{title}</div>
      <div className="flex flex-col items-center relative">
        <Gauge value={value} target={target} />
        <div className={`absolute inset-0 flex items-center justify-center pointer-events-none`}
             style={{ paddingTop: "36%" }}>
          <div className={`text-xl font-semibold tabular-nums ${metricCls}`}>{fmtK(value)}</div>
        </div>
        <div className={`text-[10px] tabular-nums w-full flex justify-between px-1
                         ${onDark ? "text-white/70" : "text-slate-500"}`}>
          <span>$0</span>
          <span>{fmtK(target)}</span>
        </div>
      </div>
      <div className="mt-2 text-center">
        <div className={`text-lg font-semibold ${metricCls}`}>
          {gmPct != null ? `${Number(gmPct).toFixed(2)}%` : "—"}
        </div>
        <div className={`text-[10px] uppercase tracking-wider
                         ${onDark ? "text-white/60" : "text-slate-400"}`}>
          Gross Margin %
        </div>
        {attainment != null && !onDark && (
          <div className={`text-[10px] mt-0.5 ${attainment >= 100 ? "text-emerald-700"
                                                                     : "text-slate-500"}`}>
            {attainment.toFixed(0)}% of target
          </div>
        )}
      </div>
    </div>
  );
}

function fmtK(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return "$" + (n / 1_000_000).toFixed(2) + "M";
  if (abs >= 1_000) return "$" + (n / 1_000).toFixed(2) + "K";
  return "$" + n.toFixed(0);
}

function formatMonthDisplay(k) {
  if (!k) return "";
  const [y, m] = k.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-AU", { month: "long", year: "numeric" });
}
